<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 py-8">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-4">
            <button @click="$emit('back')" class="p-2 hover:bg-accent rounded-md">
              <ArrowLeft class="h-5 w-5" />
            </button>
            <div class="flex-1">
              <div class="flex items-center gap-4 mb-2">
                <h1 class="text-3xl font-semibold text-gray-900">{{ talentPool.title }}</h1>
                <!-- <StatusPill :status="talentPool.status" /> -->
                <div v-if="talentPool.isProject" class="bg-blue-600 text-white px-3 py-1 text-xs font-semibold rounded">
                  Project
                </div>
              </div>
              <div class="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{{ talentPool.category }}</span>
                <span>•</span>
                <span>{{ talentPool.location }}</span>
                <span>•</span>
                <span>Created {{ talentPool.createdDate }}</span>
              </div>
            </div>
          </div>
          <button class="px-4 py-2 border border-border rounded-md hover:bg-accent hover:text-accent-foreground transition-colors flex items-center space-x-2">
            <Settings class="h-4 w-4" />
            <span>Settings</span>
          </button>
        </div>

        <!-- Description -->
        <p class="text-gray-700 mb-6 max-w-3xl">{{ talentPool.description }}</p>

        <!-- Skills -->
        <div class="flex flex-wrap gap-2 mb-8">
          <SkillTag
            v-for="(skill, index) in talentPool.skills"
            :key="index"
            :skill="skill"
          />
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-gray-900 mb-1">{{ talentPool.metrics.total }}</div>
          <div class="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <Users class="h-3 w-3" />
            Total
          </div>
        </div>
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-blue-600 mb-1">{{ talentPool.metrics.accepted }}</div>
          <div class="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <CheckCircle class="h-3 w-3" />
            Accepted
          </div>
        </div>
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-amber-600 mb-1">{{ talentPool.metrics.pending }}</div>
          <div class="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <Clock class="h-3 w-3" />
            Pending
          </div>
        </div>
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-red-600 mb-1">{{ talentPool.metrics.declined }}</div>
          <div class="text-xs text-muted-foreground">Declined</div>
        </div>
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-green-600 mb-1">{{ talentPool.metrics.avgRate }}</div>
          <div class="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <TrendingUp class="h-3 w-3" />
            Avg Rate
          </div>
        </div>
        <div class="bg-card border border-border rounded-lg shadow-sm p-4 text-center">
          <div class="text-2xl font-semibold text-purple-600 mb-1">{{ talentPool.metrics.avgExperience }}</div>
          <div class="text-xs text-muted-foreground">Avg Experience</div>
        </div>
      </div>

      <!-- Contractors Section -->
      <div class="bg-card border border-border rounded-lg shadow-sm">
        <div class="p-6 pb-2">
          <div class="flex items-center justify-between">
            <h3 class="font-semibold">Talent Pool Members ({{ filteredContractors.length }})</h3>
            <div class="flex items-center gap-4">
              <!-- Status Filter -->
              <div class="flex gap-2">
                <button
                  v-for="status in ['All', 'Accepted', 'Pending', 'Declined']"
                  :key="status"
                  :class="[
                    'px-3 py-1 rounded-md text-xs transition-colors',
                    statusFilter === status 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-white border border-border text-foreground hover:bg-accent'
                  ]"
                  @click="statusFilter = status"
                >
                  {{ status }}
                </button>
              </div>
              
              <!-- Search -->
              <div class="relative w-80">
                <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  v-model="searchQuery"
                  placeholder="Search members..."
                  class="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="p-6 pt-0">
          <!-- Contractors Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ContractorCard
              v-for="contractor in filteredContractors"
              :key="contractor.id"
              :contractor="contractor"
              @view-profile="handleViewProfile"
              @download-resume="handleDownloadResume"
              @message="handleMessage"
            />
          </div>

          <!-- Empty state -->
          <div v-if="filteredContractors.length === 0" class="text-center py-12">
            <div class="text-gray-500 text-lg">No members found</div>
            <div class="text-gray-400 text-sm mt-2">
              Try adjusting your search or filters
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
// import { ArrowLeft, Search, Users, TrendingUp, Clock, CheckCircle, Settings } from 'lucide-vue-next'
// import StatusPill from './StatusPill.vue'
// import SkillTag from './SkillTag.vue'
// import ContractorCard from './ContractorCard.vue'

// Props
const props = defineProps({
  talentPoolId: {
    type: String,
    required: true
  }
})

// Emits
defineEmits(['back'])

// Reactive data
const searchQuery = ref('')
const statusFilter = ref('All')

// Mock talent pool data (in real app, this would be fetched based on talentPoolId)
const talentPool = {
  id: props.talentPoolId,
  title: 'Senior Frontend Developer',
  status: 'Active',
  category: 'Technology',
  location: 'Sydney, NSW',
  description: 'Seeking experienced frontend developers with strong React and TypeScript skills for various client projects.',
  skills: ['React', 'TypeScript', 'Next.js', 'Tailwind CSS', 'GraphQL', 'Node.js', 'AWS'],
  isProject: true,
  createdDate: 'February 15, 2024',
  isPublic: true,
  metrics: {
    total: 45,
    accepted: 12,
    pending: 8,
    declined: 5,
    avgRate: '$89',
    avgExperience: '6.5 years'
  }
}

// Computed properties
const filteredContractors = computed(() => {
  return mockContractors.filter((contractor) => {
    const matchesSearch = contractor.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                         contractor.skills.some(skill => skill.toLowerCase().includes(searchQuery.value.toLowerCase()))
    const matchesStatus = statusFilter.value === 'All' || contractor.status === statusFilter.value
    return matchesSearch && matchesStatus
  })
})

// Event handlers
const handleViewProfile = (contractor) => {
  console.log(`View profile for contractor: ${contractor?.contractorId || contractor}`)
}

const handleDownloadResume = (contractorId) => {
  console.log(`Download resume for contractor: ${contractorId}`)
}

const handleMessage = (contractorId) => {
  console.log(`Send message to contractor: ${contractorId}`)
}
</script>
